import React, { useState, useEffect } from 'react';
import { CreditCard, AlertCircle, CheckCircle, Settings } from 'lucide-react';
import Button from '../../../components/ui/Button';
import { getStripeStatus, processInvoicePayment } from '../../../services/stripeService';

/**
 * Stripe Payment Section Component
 * 
 * This component provides payment processing UI for invoices.
 * Currently uses placeholder Stripe keys - replace with actual keys before production use.
 */
export default function StripePaymentSection({ invoice, onPaymentSuccess }) {
  const [stripeStatus, setStripeStatus] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [paymentResult, setPaymentResult] = useState(null);

  useEffect(() => {
    checkStripeStatus();
  }, []);

  const checkStripeStatus = () => {
    const status = getStripeStatus();
    setStripeStatus(status);
  };

  const handleProcessPayment = async () => {
    if (!stripeStatus?.configured) {
      setPaymentResult({
        success: false,
        message: 'Stripe integration not configured. Please add your API keys.'
      });
      return;
    }

    setProcessing(true);
    setPaymentResult(null);

    try {
      const result = await processInvoicePayment({
        invoiceId: invoice?.id,
        amount: invoice?.totalAmount,
        currency: 'usd',
        customerId: invoice?.customerId,
        invoiceNumber: invoice?.invoiceNumber
      });

      setPaymentResult(result);
      
      if (result?.success && onPaymentSuccess) {
        onPaymentSuccess(result);
      }
    } catch (error) {
      setPaymentResult({
        success: false,
        message: error?.message || 'Payment processing failed'
      });
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 bg-blue-50 rounded-lg">
          <CreditCard className="w-5 h-5 text-blue-600" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Payment Processing</h3>
          <p className="text-sm text-gray-600">Secure payment via Stripe</p>
        </div>
      </div>

      {/* Stripe Status Banner */}
      {stripeStatus && (
        <div className={`mb-4 p-4 rounded-lg border ${
          stripeStatus?.configured 
            ? 'bg-green-50 border-green-200' :'bg-yellow-50 border-yellow-200'
        }`}>
          <div className="flex items-start gap-3">
            {stripeStatus?.configured ? (
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            ) : (
              <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            )}
            <div className="flex-1">
              <p className={`text-sm font-medium ${
                stripeStatus?.configured ? 'text-green-900' : 'text-yellow-900'
              }`}>
                {stripeStatus?.message}
              </p>
              {stripeStatus?.action && (
                <p className="text-sm text-yellow-700 mt-1">
                  Action required: {stripeStatus?.action}
                </p>
              )}
              {stripeStatus?.testMode && (
                <p className="text-sm text-green-700 mt-1">
                  ✓ Running in test mode - use test cards for payments
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Payment Result */}
      {paymentResult && (
        <div className={`mb-4 p-4 rounded-lg border ${
          paymentResult?.success 
            ? 'bg-green-50 border-green-200' :'bg-red-50 border-red-200'
        }`}>
          <div className="flex items-start gap-3">
            {paymentResult?.success ? (
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            ) : (
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            )}
            <div className="flex-1">
              <p className={`text-sm font-medium ${
                paymentResult?.success ? 'text-green-900' : 'text-red-900'
              }`}>
                {paymentResult?.message}
              </p>
              {paymentResult?.note && (
                <p className="text-sm text-gray-600 mt-1">
                  {paymentResult?.note}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Invoice Amount */}
      {invoice?.totalAmount && (
        <div className="mb-4 p-4 bg-gray-50 rounded-lg">
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">Total Amount</span>
            <span className="text-2xl font-bold text-gray-900">
              ${parseFloat(invoice?.totalAmount || 0)?.toFixed(2)}
            </span>
          </div>
          {invoice?.invoiceNumber && (
            <div className="mt-2 text-sm text-gray-500">
              Invoice #{invoice?.invoiceNumber}
            </div>
          )}
        </div>
      )}

      {/* Action Buttons */}
      <div className="space-y-3">
        <Button
          onClick={handleProcessPayment}
          disabled={!stripeStatus?.configured || processing || !invoice?.totalAmount}
          className="w-full flex items-center justify-center gap-2"
        >
          <CreditCard className="w-5 h-5" />
          {processing ? 'Processing...' : 'Process Payment'}
        </Button>

        {!stripeStatus?.configured && (
          <div className="text-center">
            <a
              href="https://dashboard.stripe.com/apikeys"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700"
            >
              <Settings className="w-4 h-4" />
              Configure Stripe Integration
            </a>
          </div>
        )}
      </div>

      {/* Setup Instructions */}
      {!stripeStatus?.configured && (
        <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <h4 className="text-sm font-semibold text-blue-900 mb-2">
            Setup Instructions
          </h4>
          <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
            <li>Create a Stripe account at stripe.com</li>
            <li>Get your API keys from the Stripe Dashboard</li>
            <li>Update VITE_STRIPE_PUBLISHABLE_KEY in .env file</li>
            <li>Reload the application to activate payment processing</li>
          </ol>
        </div>
      )}
    </div>
  );
}